import { Alert, Form, message, Modal, Select, Typography } from 'antd';
import { Permission } from '@aid/shared';
import type { KeycloakUserDto } from '../../../api/types';
import { useHasPermission } from '../../../auth/useHasPermission';
import { useT } from '../../../locales';
import { useRolesList } from '../../roles/api/useRoles';
import { useLinkKeycloakUser } from '../api/useKeycloakOrphans';

interface LinkKeycloakUserModalProps {
  open: boolean;
  user: KeycloakUserDto;
  onClose: () => void;
}

interface LinkFormValues {
  roleIds: string[];
}

export function LinkKeycloakUserModal({ open, user, onClose }: LinkKeycloakUserModalProps) {
  const t = useT();
  const [form] = Form.useForm<LinkFormValues>();
  const canReadRoles = useHasPermission(Permission.ROLES_READ);
  const { data: rolesData, isLoading: rolesLoading } = useRolesList(
    {
      limit: 100,
      isActive: true,
    },
    { enabled: canReadRoles },
  );
  const linkUser = useLinkKeycloakUser();

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleFinish = async (values: LinkFormValues) => {
    try {
      await linkUser.mutateAsync({ keycloakId: user.id, roleIds: values.roleIds ?? [] });
      message.success(t('pages.keycloakOrphans.linkSuccess'));
      handleCancel();
    } catch {
      message.error(t('pages.keycloakOrphans.linkError'));
    }
  };

  return (
    <Modal
      title={t('pages.keycloakOrphans.linkTitle')}
      open={open}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      confirmLoading={linkUser.isPending}
      destroyOnHidden
      okText={t('pages.keycloakOrphans.linkUser')}
      cancelText={t('common.cancel')}
      centered
      width={520}
      afterOpenChange={(nextOpen) => {
        if (!nextOpen) form.resetFields();
      }}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
    >
      <Typography.Paragraph type="secondary">
        {t('pages.keycloakOrphans.linkDescription')}
      </Typography.Paragraph>
      <Typography.Paragraph>
        <strong>{t('pages.keycloakOrphans.username')}:</strong> {user.username}
        <br />
        <strong>{t('pages.keycloakOrphans.email')}:</strong> {user.email || '-'}
        <br />
        <strong>{t('pages.keycloakOrphans.keycloakId')}:</strong> {user.id}
      </Typography.Paragraph>
      <Form<LinkFormValues>
        form={form}
        layout="vertical"
        preserve={false}
        clearOnDestroy
        onFinish={handleFinish}
        initialValues={{ roleIds: [] }}
      >
        {canReadRoles ? (
          <Form.Item name="roleIds" label={t('pages.keycloakOrphans.roleLabel')}>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder={t('pages.keycloakOrphans.rolePlaceholder')}
              loading={rolesLoading}
              options={(rolesData?.data ?? [])
                .filter((role) => role.isActive)
                .map((role) => ({
                  label: role.name,
                  value: role.id,
                }))}
            />
          </Form.Item>
        ) : (
          <Alert
            type="info"
            showIcon
            title={t('pages.keycloakOrphans.rolesUnavailableTitle')}
            description={t('pages.keycloakOrphans.rolesUnavailableDescription')}
          />
        )}
      </Form>
    </Modal>
  );
}
